import * as React from "react";
import { NavMenuOption, PageLayout } from "../../../components/PageLayout/PageLayout";
import { Typography, Box } from "@mui/material";
import { BreadcrumbItem } from "../../../components/Breadcrumbs/Breadcrumbs";
import { Filter, FilterValues } from "./Filter/Filter";
import { ConnectedLinearChart } from "../../../components/charts/ConnectedLinearChart/ConnectedLinearChart";
import { ConnectedPieChart } from "../../../components/charts/ConnectedPieChart/ConnectedPieChart";
import { ChartDataPoint } from "../../../components/charts/ChartDataPoint";
import { PieChartDataPoint } from "../../../components/charts/PieChart/PieChart";
import { ApiError, ApplicationEventType, Branch, SystemSettings, SystemSettingsService } from "../../../gen/clients";
import { describeApplicationEventType } from "../../../utils/applicationEventUtils";
import { SelectOption } from "@mui/base";
import { useQuery } from "react-query";
import Grid from "@mui/material/Grid";

const buildLinearChartQuery = (
  fromISO: string,
  toISO: string,
  openSearchFilters: Record<string, any>[],
  interval: string
): Record<string, any> => {
  const useCalendarInterval = interval === "1w" || interval === "1M";
  return {
    size: 0,
    query: {
      bool: {
        filter: [...openSearchFilters, { range: { "@timestamp": { gte: fromISO, lt: toISO } } }]
      }
    },
    aggs: {
      events_over_time: {
        date_histogram: {
          field: "@timestamp",
          ...(useCalendarInterval ? { calendar_interval: interval } : { fixed_interval: interval }),
          min_doc_count: 0,
          extended_bounds: { min: fromISO, max: toISO },
          time_zone: "America/Los_Angeles"
        }
      }
    }
  };
};

const parseLinearChartResponse = (response: any): ChartDataPoint[] => {
  const buckets = response?.aggregations?.events_over_time?.buckets ?? [];
  return buckets.map((bucket: any) => ({
    timestamp: bucket.key_as_string || new Date(bucket.key).toISOString(),
    count: bucket.doc_count as number
  }));
};

const buildClientPieChartQuery = (
  fromISO: string,
  toISO: string,
  openSearchFilters: Record<string, any>[]
): Record<string, any> => ({
  size: 0,
  query: {
    bool: {
      filter: [...openSearchFilters, { range: { "@timestamp": { gte: fromISO, lt: toISO } } }]
    }
  },
  aggs: {
    by_client: {
      terms: {
        field: "clientId",
        size: 50
      },
      aggs: {
        client_name: {
          top_hits: {
            size: 1,
            _source: ["clientName"]
          }
        }
      }
    }
  }
});

const parseClientPieChartResponse = (response: any): PieChartDataPoint[] => {
  const buckets = response?.aggregations?.by_client?.buckets ?? [];
  return buckets.map((bucket: any) => {
    const clientName = bucket.client_name?.hits?.hits?.[0]?._source?.clientName;
    return {
      name: clientName || bucket.key,
      value: bucket.doc_count as number
    };
  });
};

const buildBranchPieChartQuery = (
  fromISO: string,
  toISO: string,
  openSearchFilters: Record<string, any>[]
): Record<string, any> => ({
  size: 0,
  query: {
    bool: {
      filter: [...openSearchFilters, { range: { "@timestamp": { gte: fromISO, lt: toISO } } }]
    }
  },
  aggs: {
    by_branch: {
      terms: {
        field: "branchId",
        size: 50
      }
    }
  }
});

const parseBranchPieChartResponse = (response: any, branchNameMap: Record<string, string>): PieChartDataPoint[] => {
  const buckets = response?.aggregations?.by_branch?.buckets ?? [];
  return buckets.map((bucket: any) => ({
    name: branchNameMap[bucket.key] || bucket.key,
    value: bucket.doc_count as number
  }));
};

const buildUserPieChartQuery = (
  fromISO: string,
  toISO: string,
  openSearchFilters: Record<string, any>[]
): Record<string, any> => ({
  size: 0,
  query: {
    bool: {
      filter: [...openSearchFilters, { range: { "@timestamp": { gte: fromISO, lt: toISO } } }]
    }
  },
  aggs: {
    by_user: {
      terms: {
        field: "triggeredByUsername",
        size: 50
      }
    }
  }
});

const parseUserPieChartResponse = (response: any): PieChartDataPoint[] => {
  const buckets = response?.aggregations?.by_user?.buckets ?? [];
  return buckets.map((bucket: any) => ({
    name: bucket.key as string,
    value: bucket.doc_count as number
  }));
};

const breadcrumbs: BreadcrumbItem[] = [
  {
    path: "/analytics",
    label: "Analytics"
  },
  {
    path: "/analytics/applicationEventAnalytics",
    label: "Application Event Analytics"
  }
];

const ApplicationEventAnalyticsPage: React.FC = () => {
  const now = new Date();
  const [from, setFrom] = React.useState<Date>(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
  const [to, setTo] = React.useState<Date>(now);
  const [eventType, setEventType] = React.useState<SelectOption<ApplicationEventType>>({
    label: describeApplicationEventType(ApplicationEventType.AUTO_QUOTE_ACCEPTED),
    value: ApplicationEventType.AUTO_QUOTE_ACCEPTED
  });
  const [branchId, setBranchId] = React.useState<string>("");
  const [clientId, setClientId] = React.useState<string>("");
  const [username, setUsername] = React.useState<string>("");
  const { data: systemSettings } = useQuery<SystemSettings, ApiError>(["getSystemSettings"], {
    queryFn: SystemSettingsService.getSystemSettings
  });

  const branches = React.useMemo(() => systemSettings?.lltsBranches || [], [systemSettings?.lltsBranches]);

  const branchNameMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    branches.forEach((branch: Branch) => {
      map[branch.id] = branch.name;
    });
    return map;
  }, [branches]);

  const chartRefetchRef = React.useRef<() => void>(() => {});
  const branchPieChartRefetchRef = React.useRef<() => void>(() => {});
  const pieChartRefetchRef = React.useRef<() => void>(() => {});
  const userPieChartRefetchRef = React.useRef<() => void>(() => {});

  const onFilterChange = React.useCallback((values: FilterValues) => {
    setFrom(values.from);
    setTo(values.to);
    setEventType(values.eventType);
    setBranchId(values.branchId);
    setClientId(values.clientId);
    setUsername(values.username);
  }, []);

  const onRefresh = React.useCallback(() => {
    if (chartRefetchRef.current) {
      chartRefetchRef.current();
    }
    if (branchPieChartRefetchRef.current) {
      branchPieChartRefetchRef.current();
    }
    if (pieChartRefetchRef.current) {
      pieChartRefetchRef.current();
    }
    if (userPieChartRefetchRef.current) {
      userPieChartRefetchRef.current();
    }
  }, []);

  const openSearchFilters = React.useMemo(() => {
    const filters: Record<string, any>[] = [{ term: { eventType: eventType.value } }];
    if (branchId) {
      filters.push({ term: { branchId } });
    }
    if (clientId) {
      filters.push({ term: { clientId } });
    }
    if (username) {
      filters.push({ term: { triggeredByUsername: username } });
    }
    return filters;
  }, [eventType, branchId, clientId, username]);

  const fromISO = from.toISOString();
  const toISO = to.toISOString();

  const chartTitle = React.useMemo(() => {
    return `${eventType.label} Events Over Time`;
  }, [eventType]);

  const linearChartBuildQuery = React.useCallback(
    (interval: string) => buildLinearChartQuery(fromISO, toISO, openSearchFilters, interval),
    [fromISO, toISO, openSearchFilters]
  );

  const branchPieChartQuery = React.useMemo(
    () => buildBranchPieChartQuery(fromISO, toISO, openSearchFilters),
    [fromISO, toISO, openSearchFilters]
  );

  const parseBranchPieChart = React.useCallback(
    (response: any) => parseBranchPieChartResponse(response, branchNameMap),
    [branchNameMap]
  );

  const clientPieChartQuery = React.useMemo(
    () => buildClientPieChartQuery(fromISO, toISO, openSearchFilters),
    [fromISO, toISO, openSearchFilters]
  );

  const userPieChartQuery = React.useMemo(
    () => buildUserPieChartQuery(fromISO, toISO, openSearchFilters),
    [fromISO, toISO, openSearchFilters]
  );

  return (
    <PageLayout
      selectedNavMenu={NavMenuOption.ANALYTICS}
      title={
        <Typography variant="h6" sx={{ mb: 2 }}>
          Application Event Analytics
        </Typography>
      }
      breadcrumbs={breadcrumbs}
    >
      <Filter
        from={from}
        to={to}
        eventType={eventType}
        branches={branches}
        onChange={onFilterChange}
        onRefresh={onRefresh}
      />

      <Box sx={{ mt: 2 }}>
        <ConnectedLinearChart
          id={`application-event-${eventType.value}`}
          title={chartTitle}
          legend="Events"
          from={from}
          to={to}
          buildQuery={linearChartBuildQuery}
          parseResponse={parseLinearChartResponse}
          refetchRef={chartRefetchRef}
        />
      </Box>

      <Grid container={true} spacing={2}>
        <Grid item={true} xs={12} md={6} lg={4}>
          <ConnectedPieChart
            id={`application-event-by-branch-${eventType.value}`}
            title={`${eventType.label} Events by Branch`}
            from={from}
            to={to}
            openSearchQuery={branchPieChartQuery}
            parseResponse={parseBranchPieChart}
            refetchRef={branchPieChartRefetchRef}
          />
        </Grid>
        <Grid item={true} xs={12} md={6} lg={4}>
          <ConnectedPieChart
            id={`application-event-by-client-${eventType.value}`}
            title={`${eventType.label} Events by Client`}
            from={from}
            to={to}
            openSearchQuery={clientPieChartQuery}
            parseResponse={parseClientPieChartResponse}
            refetchRef={pieChartRefetchRef}
          />
        </Grid>
        <Grid item={true} xs={12} md={6} lg={4}>
          <ConnectedPieChart
            id={`application-event-by-user-${eventType.value}`}
            title={`${eventType.label} Events by User`}
            from={from}
            to={to}
            openSearchQuery={userPieChartQuery}
            parseResponse={parseUserPieChartResponse}
            refetchRef={userPieChartRefetchRef}
          />
        </Grid>
      </Grid>
    </PageLayout>
  );
};

export { ApplicationEventAnalyticsPage };
